import React, { useState } from 'react';
import { Input, Typography } from 'antd';

const { Text } = Typography;

/**
 * WP-10 slice 2 — the customer's note on one estimate line.
 *
 *   "and customer should be able to add notes to lines."
 *
 * One component, used by BOTH renderers on the public estimate view — the
 * mobile card list and the desktop table. They are separate render paths for
 * the same data, and a note box written twice is a note box that behaves
 * differently at 390 than at 1440 the first time either is touched.
 *
 * ── Why the value is held here and not lifted to the page ─────────────────
 *
 * The page owns the SUBMITTED notes (they go up with the approval). This
 * component owns the in-progress text. Keeping the keystroke-by-keystroke state
 * local means typing in one line's box does not re-render the whole estimate —
 * which on a phone, with twenty-odd lines and an inspection below them, is the
 * difference between a note box that types smoothly and one that drops
 * characters. `onCommit` fires on blur, so the page learns the final text
 * without seeing every keystroke.
 */
export interface CustomerLineNoteProps {
  lineId: number;
  /** The note already stored on this line, as the server last returned it. */
  initialNote?: string | null;
  /** Whether the customer may still write. False once the document is settled. */
  editable: boolean;
  /** Called on blur with the committed text (or null when cleared). */
  onCommit: (lineId: number, note: string | null) => void;
  maxLength?: number;
}

/**
 * Must match CUSTOMER_NOTE_MAX in backend/src/utils/portalCustomerNote.ts.
 *
 * The server is the authority and rejects over-length with a 422 naming the
 * limit; this stops the customer typing three pages and only then being told.
 * Both exist on purpose — the client-side one is a courtesy, not the control.
 */
export const CUSTOMER_NOTE_MAX = 2000;

export const CustomerLineNote: React.FC<CustomerLineNoteProps> = ({
  lineId,
  initialNote,
  editable,
  onCommit,
  maxLength = CUSTOMER_NOTE_MAX,
}) => {
  const [value, setValue] = useState<string>(initialNote ?? '');

  // Settled document with nothing written: render nothing rather than an empty
  // disabled box captioned "Note for the shop", which reads as a field the
  // customer failed to fill in.
  if (!editable && !(initialNote && initialNote.trim())) return null;

  if (!editable) {
    return (
      <div style={{ marginTop: 8 }} data-testid={`line-note-readonly-${lineId}`}>
        <Text type="secondary" style={{ fontSize: 12 }}>Your note</Text>
        <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{initialNote}</div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 8 }}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        Note for the shop <span style={{ fontWeight: 400 }}>(optional)</span>
      </Text>
      <Input.TextArea
        data-testid={`line-note-${lineId}`}
        aria-label={`Note for the shop about this service`}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => {
          const trimmed = value.trim();
          onCommit(lineId, trimmed === '' ? null : trimmed);
        }}
        placeholder="Anything the technician should know about this item"
        autoSize={{ minRows: 2, maxRows: 6 }}
        maxLength={maxLength}
        // 16px is the threshold below which mobile Safari zooms the viewport on
        // focus. This is the primary use environment and the note box sits
        // among twenty-odd lines; a zoom on every tap makes the page unusable.
        style={{ fontSize: 16 }}
      />
    </div>
  );
};

export default CustomerLineNote;
